<template>
  <div class="w-full h-full flex flex-col items-center">
    <div class="w-full h-full flex flex-col md:items-center md:text-center" style="max-width: 480px">
      <div class="md:text-center space-y-3 max-w-lg md:max-w-xl">
        <BaseText type="display" size="2xs">
          Create your team and invite colleagues
        </BaseText>
        <BaseText type="body" size="md" class="text-neutral-alpha-650">
          Foreplay is better with friends, invite your team to have them join your trial on Foreplay.
        </BaseText>
      </div>
      <!-- Team Name Input -->
      <BaseInputDark 
        :value="teamName"
        label="Team Name"
        placeholder="Ad Rockstars"
        :error="teamNameError"
        @input="teamName = $event"
        class="text-left mt-7 md:mt-16"
      />
      <!-- divider -->
      <div class="w-full h-0 border-t border-neutral-alpha-100 my-6" />
      <!-- Invite CTA -->
      <div class="team-invite-cta w-full flex items-center gap-1 p-2.5">
        <TeamIcon class="text-secondary-green-100" />
        <BaseText type="label" size="sm" class="text-secondary-green-100">
          Enter email addresses to invite your team!
        </BaseText>
      </div>
      <!-- Invite Email Inputs -->
      <div class="invites-list w-full flex-shrink min-h-0 flex flex-col gap-4 mt-6 overflow-y-auto p-px">
        <BaseInputDark 
          v-for="(memberEmail, index) in teamMemberEmails"
          :key="`team-member-email-${index}`"
          :value="memberEmail"
          placeholder="name@gmail.com"
          :error="emailErrors[index]"
          @input="handleEmailInput(index, $event)"
        />
      </div>
      <!-- Primary Button: Continue / Send invites & continue -->
      <button class="send-invites-btn w-full flex items-center justify-center p-2 mt-6 h-10"
      @click="handleSubmit" :disabled="loadingSubmit">
        <BaseLoadingSpinnerCircle v-if="loadingSubmit" :duration="1" />
        <BaseText v-else type="label" size="md">
          {{ hasInviteEmails ? 'Send invites & continue' : 'Continue' }}
        </BaseText>
      </button>
      <!-- Skip Button: creates the team without sending invites -->
      <button class="w-full flex items-center justify-center p-2 mt-3 transition-colors duration-100 hover:bg-neutral-alpha-25 mb-8 md:mb-0" style="border-radius: 10px;"
      @click="handleSkip" :disabled="loadingSubmit">
        <BaseText type="label" size="md" class="text-neutral-alpha-650">
          Skip
        </BaseText>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import FunctionsAPI from '@/api'
import ForeplayTeamAPI from '@/api/foreplayServer/TeamRequests'

// Icons
import TeamIcon from '../../globals/Icons/SettingsIcons/TeamSettingsIcon.vue'

export default {
  name: 'TeamOnboardSection',
  components: {
    TeamIcon
  },
  data () {
    return {
      teamName: '',
      teamMemberEmails: ['', '', ''],
      submitAttempted: false,
      emailErrors: [false, false, false],
      loadingSubmit: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getTeam']),
    trimmedTeamName () {
      return this.teamName?.trim() || ''
    },
    inviteEmails () {
      return this.teamMemberEmails.map(email => email?.trim()).filter(email => email?.length)
    },
    hasInviteEmails () {
      return this.inviteEmails.length > 0
    },
    teamNameError () {
      return !!(this.submitAttempted && !this.trimmedTeamName.length)
    }
  },
  methods: {
    ...mapActions('AuthModule', ['fetchUserTeam']),
    // Creates the user's team if they don't already have one (falls back to their email as the name)
    async ensureTeam () {
      if (this.getTeam) return
      await ForeplayAPI.Teams.create(this.trimmedTeamName || this.getUser?.email)
      await this.fetchUserTeam()
      FunctionsAPI.Teams.migrateUserToTeam(this.getTeam.id)
    },
    async handleSkip () {
      this.loadingSubmit = true
      try {
        await this.ensureTeam()
        if (this.getTeam?.id) {
          ForeplayTeamAPI.setupWorkspace(this.getTeam.id, [])
            .catch(e => console.error('[Attio] workspace setup failed:', e))
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingSubmit = false
        this.$emit('next')
      }
    },
    handleEmailInput (index, value) {
      this.teamMemberEmails = [...this.teamMemberEmails.map((email, i) => i === index ? value : email)]
      this.validateEmailInputs()
    },
    validateEmailInputs () {
      if (!this.submitAttempted) return
      const emailRegex = /^[^\s@]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
      this.emailErrors = this.teamMemberEmails.map(email => !!(email?.trim().length && !emailRegex.test(email.trim())))
    },
    async handleSubmit () {
      // Check for validity
      this.submitAttempted = true
      this.validateEmailInputs()
      const hasEmailErrors = this.emailErrors.some(error => error)
      if (this.teamNameError) {
        this.$showAlert({ type: 'error', message: 'Please enter a team name.' })
      }
      if (hasEmailErrors) {
        this.$showAlert({ type: 'error', message: 'Ensure all invite emails are valid.' })
      }
      if (this.teamNameError || hasEmailErrors) return

      // Submit to API
      this.loadingSubmit = true
      try {
        await this.ensureTeam()

        const invitedEmails = this.inviteEmails

        ForeplayTeamAPI.setupWorkspace(this.getTeam.id, invitedEmails)
          .catch(e => console.error('[Attio] workspace setup failed:', e))

        // Invite the team members
        await Promise.all(invitedEmails.map(email => (
          FunctionsAPI.Invitations.createInvitation(email, this.getTeam.id)
        )))

        this.$emit('next')
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: this.hasInviteEmails ? 'Something went wrong sending your invitations.' : 'Something went wrong creating your team.'
        })
      } finally {
        this.loadingSubmit = false
      }
    }
  }
}
</script>

<style scoped>
.header-text {
  /* Display/H4 */
  font-family: Inter;
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: 36px; /* 128.571% */
  letter-spacing: -0.197px;
}
.team-invite-cta {
  border-radius: 10px;
  background-color: rgba(0, 199, 169, 0.12);
}

@media (min-width: 768px) {
  .invites-list {
    max-height: calc(100vh - 680px);
  }
}

.invites-list::-webkit-scrollbar {
  width: 12px;
}
.invites-list::-webkit-scrollbar-track {
  background: #020308;
}
.invites-list::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.32);
  border-radius: 10px;
  border: 4px solid #020308;
}
.invites-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.48);
}

.send-invites-btn {
  background-color: #1F69FF;
  color: white;
  border-radius: 10px;
  box-shadow: none;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
</style>